// WHICH WORKSPACE YOU ARE LOOKING AT — one answer, held above every screen.
//
// A WORKSPACE WAS A PLACE AND IT SHOULD BE A LENS. It was a control inside the
// Tables screen, which meant it only existed while you were on that screen: open
// Interfaces and there was no workspace at all, so the screen had to invent one
// (the first in the list) or ask again. Two screens about the same rows, each
// with its own idea of which rows. Lifting it to the top bar makes it what it
// always was — the scope everything below is read through — and gives it the one
// state a per-screen picker could never hold.
//
// THAT STATE IS "ALL". Airtable, Notion and Linear all make you pick exactly one
// workspace and then hide everything else, which is right for a big team and
// wrong for one person with six of them: the question "where did I put that
// table" is unanswerable from inside any single workspace. "All workspaces" is
// what makes the search in ⌘K global — see the note there. It is the default for
// exactly that reason.
//
// PERSISTED, because a scope you have to re-pick on every reload is a scope
// nobody sets. Stored by id; an id that no longer resolves falls back to "all"
// rather than to an empty screen.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { bloxxData, type Workspace } from "@/lib/bloxxData";

/** `null` is "all workspaces", not "none chosen". There is no "none". */
export type Scope = string | null;

const STORAGE_KEY = "bloxx_workspace_scope";
/**
 * WHICH WEBSITE, under the workspace (nav v2's switcher, GA-style: account, then property). A website ref from
 * GET /v1/websites, or null for All sites. Persisted the same way and for the same reason as the workspace. The
 * address wins over it: a URL that names a site (or means All sites) sets it, see components/shell.
 */
const SITE_KEY = "bloxx_site_scope";

interface ScopeValue {
  /** The chosen workspace id, or null for all of them. */
  scope: Scope;
  setScope: (next: Scope) => void;
  /** Every workspace the signed-in user is a member of. Empty while loading. */
  workspaces: Workspace[];
  /** The chosen one, or undefined when the scope is "all". */
  current?: Workspace;
  /** True when nothing is narrowing the view. What makes search global. */
  isAll: boolean;
  loading: boolean;
  /**
   * The workspace list could not be read.
   *
   * SEPARATE FROM "you have none", because they are indistinguishable downstream
   * and mean opposite things. With `retry: false` a failed read leaves
   * `workspaces` empty, so every screen reading `inScope` sees exactly what a
   * brand-new account sees and tells the user they have nothing — observed for
   * real when GET /workspaces started 500ing: the Interfaces screen calmly said
   * "Nothing published yet" about a workspace full of them.
   */
  failed: boolean;
  /**
   * The workspaces a screen should read, which is "the one you picked" or "all of
   * them" — so a screen never has to branch on `isAll` itself.
   */
  inScope: Workspace[];
  /** The chosen website's ref, or null for All sites. */
  site: string | null;
  setSite: (ref: string | null) => void;
}

const Ctx = createContext<ScopeValue | null>(null);

export function ScopeProvider({ children }: { children: React.ReactNode }) {
  const cache=useQueryClient();
  const [scope, setScopeState] = useState<Scope>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });

  /**
   * SHARES ITS QUERY KEY WITH THE TABLES SCREEN on purpose. Both ask the same
   * question and TanStack Query dedupes them, so hoisting the scope did not add a
   * request — it removed the second screen's need to make one.
   */
  const [site, setSiteState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(SITE_KEY) || null;
    } catch {
      return null;
    }
  });
  const setSite = useCallback((ref: string | null) => {
    setSiteState(ref);
    try {
      if (ref) localStorage.setItem(SITE_KEY, ref);
      else localStorage.removeItem(SITE_KEY);
    } catch {
      /* private mode */
    }
  }, []);

  const wsQ = useQuery({
    queryKey: ["data", "workspaces"],
    queryFn: () => bloxxData.listWorkspaces(),
    staleTime: 5 * 60 * 1000,
    // The data layer is a separate service from the identity one. A user who has
    // never opened Tables has no workspaces and that is not an error worth
    // retrying three times on every page load.
    retry: false,
  });

  const workspaces = useMemo(() => wsQ.data?.workspaces ?? [], [wsQ.data]);

  const setScope = useCallback((next: Scope) => {
    void cache.cancelQueries({predicate:q=>['integration-readiness','identity','integrations','sites','site-integrations-all','gsc-properties'].includes(String(q.queryKey[0]))||String(q.queryKey[0]).startsWith('social-')});
    setScopeState(next);
    try {
      if (next) localStorage.setItem(STORAGE_KEY, next);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* private mode */
    }
  }, [cache]);

  useEffect(()=>{const changed=(event:StorageEvent)=>{if(event.key===STORAGE_KEY)setScope(event.newValue||null);};window.addEventListener('storage',changed);return()=>window.removeEventListener('storage',changed);},[setScope]);

  /**
   * A STORED ID THAT NO LONGER EXISTS IS THE FAILURE MODE THAT MATTERS: a
   * workspace someone left, or deleted from another tab, would otherwise scope
   * every screen to nothing and look exactly like "you have no data". Only once
   * the list has actually loaded, or the first render would clear it every time.
   */
  useEffect(() => {
    if (!scope || !wsQ.isSuccess) return;
    if (!workspaces.some((w) => w.id === scope)) setScope(null);
  }, [scope, workspaces, wsQ.isSuccess, setScope]);

  const value = useMemo<ScopeValue>(() => {
    const current = scope ? workspaces.find((w) => w.id === scope) : undefined;
    return {
      scope,
      setScope,
      workspaces,
      current,
      isAll: !scope,
      loading: wsQ.isLoading,
      failed: wsQ.isError,
      inScope: current ? [current] : workspaces,
      site,
      setSite,
    };
  }, [scope, setScope, workspaces, wsQ.isLoading, wsQ.isError, site, setSite]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * Usable outside the provider — the dev harnesses and the auth screens render no
 * chrome at all, and a hook that throws there would make every fixture route need
 * a provider it has no use for.
 */
export function useScope(): ScopeValue {
  return (
    useContext(Ctx) ?? {
      scope: null,
      setScope: () => {},
      workspaces: [],
      current: undefined,
      isAll: true,
      loading: false,
      failed: false,
      inScope: [],
      site: null,
      setSite: () => {},
    }
  );
}
